<template>
  <Dialog
    :open="open"
    @update:open="handleUpdateOpen"
  >
    <DialogContent
      class="max-w-lg overflow-hidden rounded-[1.4rem] border border-[#d8e4f0] bg-white p-0 shadow-[0_28px_80px_rgba(15,23,42,0.28)]"
      :show-close-button="!!error"
    >
      <div class="relative overflow-hidden p-6 sm:p-8">
        <!-- Glowing subtle background aura -->
        <div
          class="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-gradient-to-br from-[#2563eb]/15 to-[#38bdf8]/10 blur-2xl"
        />
        <div
          class="pointer-events-none absolute -bottom-12 -left-12 h-44 w-44 rounded-full bg-gradient-to-tr from-[#0f3557]/10 to-[#60a5fa]/10 blur-2xl"
        />

        <!-- Loading State -->
        <div
          v-if="!error"
          class="relative z-10 flex flex-col items-center text-center"
        >
          <!-- Animated Multi-Ring Spinner -->
          <div class="relative mb-6 flex h-20 w-20 items-center justify-center">
            <div class="absolute inset-0 animate-ping rounded-full bg-[#2563eb]/10" />
            <div class="absolute inset-1 animate-spin rounded-full border-4 border-[#e2e8f0] border-t-[#0f3557] [animation-duration:1.2s]" />
            <div class="absolute inset-3 animate-spin rounded-full border-4 border-transparent border-r-[#2563eb] [animation-duration:0.8s] [animation-direction:reverse]" />
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-[#f0f6ff] text-[#0f3557]">
              <Sparkles class="h-5 w-5 animate-pulse text-[#2563eb]" />
            </div>
          </div>

          <!-- Header Titles -->
          <h3 class="text-xl font-black tracking-tight text-[#0f172a]">
            Generating Monthly Report
          </h3>
          <p class="mt-1 text-sm font-semibold text-[#64748b]">
            {{ clientName }} &bull; {{ monthLabel }}
          </p>

          <!-- Current Step Status -->
          <div class="mt-6 w-full rounded-xl border border-[#e2e8f0] bg-[#f8fbff] p-4 text-left shadow-sm">
            <div class="flex items-center justify-between text-xs font-bold text-[#475569]">
              <span class="flex items-center gap-2">
                <Loader2 class="h-3.5 w-3.5 animate-spin text-[#2563eb]" />
                <span>{{ currentStepText }}</span>
              </span>
              <span class="font-extrabold text-[#0f3557]">{{ progressPercentage }}%</span>
            </div>

            <!-- Progress Bar -->
            <div class="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#e2e8f0]">
              <div
                class="h-full rounded-full bg-gradient-to-r from-[#0f3557] via-[#2563eb] to-[#38bdf8] transition-all duration-500 ease-out"
                :style="{ width: `${progressPercentage}%` }"
              />
            </div>

            <!-- Steps checklist preview -->
            <div class="mt-4 space-y-2 text-xs">
              <div
                v-for="(step, idx) in steps"
                :key="idx"
                class="flex items-center gap-2 transition-opacity duration-300"
                :class="idx <= activeStepIndex ? 'text-[#1e293b] font-medium' : 'text-[#94a3b8] opacity-60'"
              >
                <CheckCircle2
                  v-if="idx < activeStepIndex"
                  class="h-3.5 w-3.5 flex-shrink-0 text-[#16a34a]"
                />
                <div
                  v-else-if="idx === activeStepIndex"
                  class="h-3.5 w-3.5 flex-shrink-0 rounded-full border-2 border-[#2563eb] border-t-transparent animate-spin"
                />
                <div
                  v-else
                  class="h-3.5 w-3.5 flex-shrink-0 rounded-full border border-[#cbd5e1]"
                />
                <span>{{ step }}</span>
              </div>
            </div>
          </div>

          <!-- Reassurance note -->
          <p class="mt-5 text-xs text-[#64748b]">
            Please wait while we process metrics, render charts, and compile the final layout.
          </p>
        </div>

        <!-- Error State -->
        <div
          v-else
          class="relative z-10 flex flex-col items-center text-center"
        >
          <div class="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#fef2f2] text-[#b42318] shadow-sm">
            <AlertTriangle class="h-7 w-7" />
          </div>

          <h3 class="text-xl font-black text-[#0f172a]">
            Report Generation Failed
          </h3>
          <p class="mt-1 text-sm text-[#64748b]">
            {{ clientName }} &bull; {{ monthLabel }}
          </p>

          <div class="mt-4 w-full rounded-xl border border-[#fecaca] bg-[#fff5f5] p-4 text-left text-xs font-semibold text-[#b42318]">
            {{ error }}
          </div>

          <div class="mt-6 flex w-full justify-end gap-3">
            <button
              type="button"
              class="inline-flex w-full items-center justify-center rounded-[0.9rem] bg-[#0f3557] px-4 py-2.5 text-sm font-extrabold text-white transition-colors hover:bg-[#1a4a6e]"
              @click="$emit('dismiss')"
            >
              Return to Report Builder
            </button>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { Sparkles, Loader2, CheckCircle2, AlertTriangle } from "lucide-vue-next";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const props = defineProps<{
  open: boolean;
  clientName?: string;
  monthLabel?: string;
  error?: string | null;
}>();

const emit = defineEmits<{
  (event: "update:open", value: boolean): void;
  (event: "dismiss"): void;
}>();

const steps = [
  "Uploading assets & data sources",
  "Extracting metrics & analyzing performance",
  "Synthesizing highlights & recommendations",
  "Compiling publication layout & branding",
  "Finalizing interactive report",
];

const progressPercentage = ref(10);
const activeStepIndex = ref(0);
let intervalId: any = null;

const currentStepText = computed(() => {
  return steps[activeStepIndex.value] || steps[steps.length - 1];
});

function startProgress() {
  progressPercentage.value = 12;
  activeStepIndex.value = 0;

  if (intervalId) clearInterval(intervalId);

  intervalId = setInterval(() => {
    if (progressPercentage.value < 92) {
      // Advance percentage smoothly with a bit of randomness
      const increment = Math.floor(Math.random() * 8) + 4;
      progressPercentage.value = Math.min(progressPercentage.value + increment, 92);

      // Advance step index according to progress
      if (progressPercentage.value > 75) {
        activeStepIndex.value = 3;
      } else if (progressPercentage.value > 45) {
        activeStepIndex.value = 2;
      } else if (progressPercentage.value > 20) {
        activeStepIndex.value = 1;
      }
    }
  }, 1200);
}

function stopProgress() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen && !props.error) {
      startProgress();
    } else {
      stopProgress();
    }
  },
  { immediate: true }
);

watch(
  () => props.error,
  (hasErr) => {
    if (hasErr) {
      stopProgress();
    }
  }
);

onUnmounted(() => {
  stopProgress();
});

function handleUpdateOpen(value: boolean) {
  if (props.error) {
    emit("update:open", value);
    emit("dismiss");
  }
}
</script>
